import { ArrowRightOnRectangle, ChevronDownMini } from '@medusajs/icons';
import { clx } from '@medusajs/ui';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import type { EnhancedStoreCustomer } from '@marxbiotech/shared-types/medusa';
import { Divider } from '@modules/common/components/divider';
import { useLogout } from '@lib/query/mutations/customer';

import * as Icons from './icons';
import type { AccountNavGroupItem, AccountNavItem } from './types';

export type { AccountNavItem };

function isNavGroupItem(item: AccountNavItem): item is AccountNavGroupItem {
  return item.isGroup === true;
}

export const AccountNav = ({
  customer,
  navItems,
}: {
  customer: EnhancedStoreCustomer | null;
  navItems: AccountNavItem[];
}) => {
  const location = useLocation();
  const { countryCode } = useParams<{ countryCode: string }>();
  const route = location.pathname;

  const logout = useLogout({
    onSuccess: () => {
      // Navigate to home after logout
      window.location.href = `/${countryCode ?? 'tw'}`;
    },
  });

  const handleLogout = () => {
    logout.mutate();
  };

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    navItems.filter(isNavGroupItem).reduce(
      (accumulator, item) => ({
        ...accumulator,
        [item.label]: item.links.some((link) => route.includes(link.href)),
      }),
      {} as Record<string, boolean>
    )
  );

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <div className="mb-6 mr-6 w-full rounded-md border px-2 py-2 lg:w-auto">
      <div className="small:block" data-testid="account-nav">
        <div>
          <div className="px-2 py-3">
            <div className="text-grey-30 mb-1 text-xs">目前登入的帳戶</div>
            <div className="text-black-10 truncate text-sm">
              {customer?.email}
            </div>
          </div>
          <Divider className="px-3" />
          <div>
            <ul className="mb-0 mt-1 flex flex-col items-start justify-start gap-y-1">
              {navItems.map((item) => {
                const { label, icon, dataTestId } = item;
                const Icon = Icons[icon];
                return (
                  <li key={label} className="w-full select-none">
                    {isNavGroupItem(item) ? (
                      <>
                        <div
                          className="text-black-10 hover:text-grey-30 flex cursor-pointer items-center px-2 py-3"
                          onClick={() => toggleGroup(label)}
                        >
                          <Icon className="text-black-10 my-auto mr-2 border-0" />
                          <span className="mr-2">{label}</span>
                          <ChevronDownMini
                            className={clx(
                              'ml-auto transition-transform duration-200 ease-in',
                              {
                                'rotate-[-180deg]': expandedGroups[label],
                              }
                            )}
                          />
                        </div>
                        {expandedGroups[label] &&
                          item.links.map(({ href, label, dataTestId }) => (
                            <AccountNavLink
                              key={label}
                              href={href}
                              route={route}
                              countryCode={countryCode ?? 'tw'}
                              data-testid={dataTestId}
                            >
                              {({ active }) => (
                                <div
                                  className={clx(
                                    'text-grey-60 hover:text-grey-30 flex rounded-md px-2 py-2 pl-10 text-sm',
                                    {
                                      'bg-white-30': active,
                                    }
                                  )}
                                >
                                  {label}
                                </div>
                              )}
                            </AccountNavLink>
                          ))}
                      </>
                    ) : (
                      <AccountNavLink
                        href={item.href}
                        route={route}
                        countryCode={countryCode ?? 'tw'}
                        data-testid={dataTestId}
                      >
                        {({ active }) => (
                          <div
                            className={clx(
                              'text-black-10 hover:text-grey-30 flex rounded-md px-2 py-3',
                              {
                                'bg-white-30': active,
                              }
                            )}
                          >
                            <Icon className="my-auto mr-2 border-0" />
                            {label}
                          </div>
                        )}
                      </AccountNavLink>
                    )}
                  </li>
                );
              })}
              <Divider />
              <li className="w-full select-none">
                <div
                  role="button"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className={clx(
                    'text-black-10 hover:text-grey-30 flex cursor-pointer px-2 py-3',
                    { 'opacity-50': logout.isPending }
                  )}
                >
                  <ArrowRightOnRectangle className="my-auto mr-2 border-0" />
                  {logout.isPending ? '登出中...' : '登出'}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

type AccountNavLinkProps = {
  href: string;
  route: string;
  countryCode: string;
  children: (props: { active: boolean }) => React.ReactNode;
  'data-testid'?: string;
};

const AccountNavLink = ({
  href,
  route,
  countryCode,
  children,
  'data-testid': dataTestId,
}: AccountNavLinkProps) => {
  // Build the full path with country code
  const fullPath = `/${countryCode}${href}`;
  // Check if current route matches this link
  // For root account path (/account), use exact match only to avoid
  // matching all sub-pages like /account/addresses, /account/orders, etc.
  const isRootAccountPath = href === '/account';
  const active = isRootAccountPath
    ? route === fullPath
    : route === fullPath || route.startsWith(`${fullPath}/`);

  return (
    <Link to={fullPath} className="block" data-testid={dataTestId}>
      {children({ active })}
    </Link>
  );
};
